import React from 'react';
import PropTypes from 'prop-types';
import Box from 'common/components/Box';
import Text from 'common/components/Text';
import Heading from 'common/components/Heading';
import Container from 'common/components/UI/Container';
import {
  CaseStudiesWrapper,
  Intro,
  CardGrid,
  Card,
  ChartCard,
  CaseCta,
  Footnote,
} from './casestudies.style';
import TrialDownloadButton from '../../Custom/TrialDownloadButton';

// Use cases from Paweł Zubkiewicz's AWS cost audits and projects. These were
// his own projects and the savings were made, so every card says "saved"; the
// amounts stay as the sources give them. No client is named and no data size
// is given. The "In CloudPouch" line names only the kind of finding the app
// shows (checked in the app code), never the amount or the audit plan.
const CASE_STUDIES = [
  {
    amount: '$1.3M',
    amountLabel: 'saved a year',
    story:
      'Thousands of stopped EC2 instances still had EBS volumes attached, and EBS alone cost about $150,000 a month. Snapshot first, then delete: 19,136 unused volumes were removed, saving $109,895 a month.',
    app: 'EBS volumes on stopped instances, with their monthly cost.',
  },
  {
    amount: '$18,890',
    amountLabel: 'saved a month',
    story:
      'An account with about 17,000 EBS volumes paid about $100,000 a month for gp2. CloudPouch pointed to gp3, and the move at the end of 2021 cut that cost by about 20%.',
    app: 'gp2 volumes that are cheaper on gp3, with the monthly saving.',
  },
  {
    amount: '13%',
    amountLabel: 'of the bill saved',
    story:
      'Industrial company, about $48,400 a month on AWS (October 2025), already well managed. The audit found $6,017 a month: an Athena results lifecycle, fewer costly S3 requests, failing Glue jobs, rightsizing, and 37 of 72 load balancers unused.',
    app: 'unused load balancers, and Glue jobs with their failed runs.',
  },
  {
    amount: '$153,416',
    amountLabel: 'saved a month on EC2',
    story:
      'Big-tech company, about $1.5M a month on AWS (April 2024). The audit found 32% of EC2 cost, plus $65,370 a month on RDS: 779 oversized EC2 instances and 90 unused databases.',
    app: 'oversized EC2 instances and RDS databases with no connections.',
  },
  {
    amount: '44%',
    amountLabel: 'of the bill saved',
    story:
      'After an EKS rollout (2023), an industrial company saw RDS costs rise 63% and CloudWatch 85%. The changes: RDS 62% less, Glue 69% less (crawlers up to 99% less with a new schedule), ELB 43% less.',
    app: 'Glue crawler schedules and runs, unused RDS databases and load balancers.',
  },
];

// The bill from a project Paweł worked on, shown as his screenshot from
// CloudPouch (May 2023 $7,902.17, February 2024 $2,959.53). It stands apart
// from the audit cards: it is not one of them and the app is not credited
// with the saving. The image keeps its own wording; the page says "less".
const BILL = {
  before: '$7,902.17',
  after: '$2,959.53',
  difference: '$4,942.65',
  percent: '62.55%',
  // The top of the screenshot only (the chart); the figures are in the text.
  // The full screenshot, with its table, stays in /images/ as it was.
  image: '/images/cloudpouch-bill-may-2023-feb-2024-chart.png',
  width: 1646,
  height: 358,
};

// The same download button as in the hero, after the proof.
const CTA_BUTTON = {
  type: 'button',
  fontSize: '16px',
  fontWeight: '700',
  borderRadius: '6px',
  pl: '28px',
  pr: '28px',
  colors: 'secondaryWithBg',
  minHeight: 'auto',
  height: '54px',
};

const CaseStudiesSection = ({ sectionWrapper, secTitleWrapper, secText, secHeading }) => {
  return (
    <CaseStudiesWrapper id="case_studies">
      <Container>
        <Box {...sectionWrapper}>
          <Box {...secTitleWrapper}>
            <Text {...secText} content="USE CASES" />
            <Heading {...secHeading} content="What AWS cost audits and projects found" />
            <Intro>
              Results from AWS cost audits and projects by CloudPouch&apos;s
              founder. Each card shows what the app finds for you.
            </Intro>
          </Box>
          <CardGrid>
            {CASE_STUDIES.map((item) => (
              <Card key={item.amount}>
                <span className="amount">{item.amount}</span>
                <span className="amount-label">{item.amountLabel}</span>
                <p>{item.story}</p>
                <p className="in-app">
                  <b>In CloudPouch:</b> {item.app}
                </p>
              </Card>
            ))}
          </CardGrid>
        </Box>
      </Container>
      <ChartCard id="bill_chart" aria-labelledby="bill-chart-heading">
        <div className="chart-copy">
          <div className="chart-head">
            <h3 id="bill-chart-heading">
              {BILL.percent} less on the monthly AWS bill
            </h3>
            <p className="chart-diff">{BILL.difference} less a month</p>
          </div>
          <blockquote>
            <p>
              “In one of the projects I worked on, the monthly AWS bill
              dropped from {BILL.before} in May 2023 to {BILL.after} in
              February 2024.”
            </p>
            <footer>Paweł Zubkiewicz, founder of CloudPouch</footer>
          </blockquote>
        </div>
        <figure className="chart-shot">
          <a href={BILL.image} target="_blank" rel="noopener">
            <img
              src={BILL.image}
              width={BILL.width}
              height={BILL.height}
              loading="lazy"
              decoding="async"
              alt={`CloudPouch chart of the monthly AWS bill by service: ${BILL.before} in May 2023 and ${BILL.after} in February 2024, ${BILL.percent} less.`}
            />
          </a>
          <figcaption>
            Screenshot from CloudPouch: monthly AWS bill, May 2023 vs February
            2024.
          </figcaption>
        </figure>
      </ChartCard>
      <Container>
        <Box>
          <CaseCta>
            <p>Run the same checks on your own AWS account.</p>
            <TrialDownloadButton buttonStyle={CTA_BUTTON} location="case_studies" />
          </CaseCta>
          <Footnote>
            Results from past audits and projects are not guarantees; yours
            depend on your usage and current AWS pricing. Clients are not named.
          </Footnote>
        </Box>
      </Container>
    </CaseStudiesWrapper>
  );
};

CaseStudiesSection.propTypes = {
  sectionWrapper: PropTypes.object,
  secTitleWrapper: PropTypes.object,
  secText: PropTypes.object,
  secHeading: PropTypes.object,
};

CaseStudiesSection.defaultProps = {
  sectionWrapper: {},
  secTitleWrapper: {
    mb: ['30px', '40px'],
  },
  secText: {
    as: 'span',
    display: 'block',
    textAlign: 'center',
    fontSize: '14px',
    letterSpacing: '0.15em',
    fontWeight: '700',
    color: '#46679f',
    mb: '5px',
  },
  secHeading: {
    textAlign: 'center',
    fontSize: ['20px', '24px'],
    fontWeight: '500',
    color: '#0f2137',
    letterSpacing: '-0.025em',
    mb: '0',
    lineHeight: '1.67',
  },
};

export default CaseStudiesSection;
